import {
  APP_MENU_URL,
  SHELL_OVERLAY_EVENT,
  THEME_LINE_PREFIX,
  TITLE_BAR_HEIGHT as NATIVE_TITLE_BAR_HEIGHT,
  TRAFFIC_LIGHT_INSET,
  WINDOW_CONTROLS_INSET_VAR,
  WINDOW_CONTROLS_MARGIN,
  WINDOWS_CAPTION_WIDTH,
} from "./shellContract";

/**
 * Where the app is running, and whether it has to draw the window's top bar.
 *
 * The Electron shell suppresses the OS title bar on macOS and Windows, so the
 * app owns the top band there: drag regions plus room for the traffic lights
 * (macOS, top-left) or the caption buttons (Windows, top-right). A browser tab
 * — and the shell on Linux, which keeps its native frame — reserves nothing.
 *
 * There is no preload by design (remote login pages must get no privileged
 * surface), hence the user-agent sniff: it is the only signal a sandboxed page
 * has. Everything the shell also has to know is in shellContract.
 */

export { WINDOWS_CAPTION_WIDTH };

const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;

const isDesktopShell = /Electron\//.test(userAgent);

export const isMacDesktopShell = isDesktopShell && userAgent.includes("Macintosh");
export const isWindowsDesktopShell = isDesktopShell && userAgent.includes("Windows");

// Native window buttons stay in screen units while page zoom changes CSS units.
// The macOS hidden-inset window has no side frame, so these widths give the
// inverse zoom factor without confusing page zoom with Retina display scaling.
if (isMacDesktopShell && typeof window !== "undefined") {
  const syncZoom = () => {
    document.documentElement.style.setProperty(
      "--desktop-zoom-correction",
      String(window.innerWidth / window.outerWidth),
    );
  };
  syncZoom();
  window.addEventListener("resize", syncZoom);
}

const zoomCorrection = "var(--desktop-zoom-correction, 1)";

// Keep the native buttons inside the title-bar band even at low page zoom.
export const TITLE_BAR_HEIGHT = `max(${NATIVE_TITLE_BAR_HEIGHT}px, calc(${NATIVE_TITLE_BAR_HEIGHT}px * ${zoomCorrection}))`;

/**
 * Where the app's window controls start, whichever element hosts them. A CSS
 * length rather than a number: the traffic lights need that room only while
 * they are on screen, and fullscreen takes them away with nothing the page can
 * observe, so the shell gets to shrink it — see WINDOW_CONTROLS_INSET_VAR.
 */
export const WINDOW_CONTROLS_INSET = `calc(var(${WINDOW_CONTROLS_INSET_VAR}, ${
  isMacDesktopShell ? TRAFFIC_LIGHT_INSET : WINDOW_CONTROLS_MARGIN
}px) * ${zoomCorrection})`;

export const hasCustomTitleBar = isMacDesktopShell || isWindowsDesktopShell;

/**
 * macOS has the app-level commands in the application menu; the other shells
 * have no menu bar, and a browser tab has no shell to ask.
 */
export const hasAppMenuButton = isDesktopShell && !isMacDesktopShell;

/** Asks the shell to open the native app menu, anchored under `button`. */
export const openAppMenu = (button: HTMLElement): void => {
  const { left, bottom } = button.getBoundingClientRect();
  // Intercepted by the shell and never loaded; see desktop/src/app-menu.ts.
  window.location.href = `${APP_MENU_URL}?x=${Math.round(left)}&y=${Math.round(bottom)}`;
};

/**
 * Listens for the shell asking to raise the overlay a path stands for over
 * whatever page is on screen, and hands the handler that path. Returns the
 * unsubscribe. Inert in a browser tab, where nothing dispatches the event.
 */
export const onShellOverlay = (handler: (pathname: string) => void): (() => void) => {
  const listener = (event: Event) => {
    handler((event as CustomEvent<string>).detail);
  };
  window.addEventListener(SHELL_OVERLAY_EVENT, listener);
  return () => window.removeEventListener(SHELL_OVERLAY_EVENT, listener);
};

// Marks the document for the styling Windows needs and the other platforms
// must not get — see the scrollbar rules in app.css.
if (isWindowsDesktopShell && typeof document !== "undefined") {
  document.documentElement.classList.add("win-shell");
}

/**
 * Announces the active theme to the shell, which repaints the Windows caption
 * strip to match. A console line is the only channel a page without a preload
 * has; everywhere else this is an inert log.
 */
export const reportThemeToShell = (theme: "light" | "dark"): void => {
  if (!isWindowsDesktopShell) return;
  console.info(`${THEME_LINE_PREFIX}${theme}`);
};
